import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Stack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  NumberInput,
  NumberInputField,
  HStack,
  Text,
  Divider,
  Textarea,
  Checkbox,
  useToast,
} from "@chakra-ui/react";
import { ModalShell, CurrencyText } from "../../../shared/ui";
import { paraISOaPartirDeExibicao } from "../../../shared/lib/date";
import { apiErrorMessage } from "../../../shared/api/apiErrorMessage";
import { editarPlantaoSchema, reagendarPlantaoSchema, OBSERVACAO_MAX } from "../schemas/plantaoSchema";
import { useEditShiftMutation } from "../hooks/useEditShiftMutation";
import { useReagendamentosQuery } from "../hooks/useReagendamentosQuery";
import { useConflictGuard } from "../hooks/useConflictGuard";
import { ShiftConflictWarning, ShiftConflictFooter } from "./ShiftConflictWarning";

function valuesFromShift(shift) {
  return {
    data: paraISOaPartirDeExibicao(shift.data),
    horas: shift.horas,
    valor: shift.valor,
    horaInicio: shift.hora_inicio ?? "",
    observacao: shift.observacao ?? "",
    importante: Boolean(shift.importante),
  };
}

// Reaproveitado por Recebíveis (edição completa: data/horas/valor) e Calendário
// (`rescheduleOnly`: só a data) — as duas telas fazem o mesmo PATCH parcial
// (backend: application.EditPlantaoRequest), então um único modal evita duplicar o
// wiring de formulário/mutação/toast para uma diferença que é só "quais campos aparecem".
export default function EditShiftModal({ isOpen, onClose, shift, rescheduleOnly = false }) {
  const toast = useToast();
  const mutation = useEditShiftMutation();
  const schema = rescheduleOnly ? reagendarPlantaoSchema : editarPlantaoSchema;
  const { data: reagendamentos } = useReagendamentosQuery(shift?.id, rescheduleOnly && isOpen);
  const conflictGuard = useConflictGuard();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema) });

  // O modal fica montado o tempo todo (não é recriado por linha/dia clicado) — sincroniza
  // os campos com o plantão selecionado sempre que ele mudar.
  useEffect(() => {
    if (!shift) return;
    reset(valuesFromShift(shift));
  }, [shift, reset]);

  const close = () => {
    if (shift) reset(valuesFromShift(shift));
    conflictGuard.dismiss();
    onClose();
  };

  // A nova data/hora pode bater com outro plantão: avisa antes de salvar (o próprio
  // plantão fica fora da consulta). O reagendamento mantém a hora e as horas atuais.
  const onSubmit = (values) => {
    conflictGuard.guard(
      {
        datas: [values.data],
        horaInicio: rescheduleOnly ? shift.hora_inicio ?? "" : values.horaInicio,
        horas: rescheduleOnly ? shift.horas : values.horas,
        ignorarId: shift.id,
      },
      () => save(values)
    );
  };

  const save = (values) => {
    mutation.mutate(
      { id: shift.id, ...values },
      {
        onSuccess: () => {
          toast({ title: rescheduleOnly ? "Plantão reagendado" : "Plantão atualizado", status: "success", duration: 4000 });
          close();
        },
        onError: (error) =>
          toast({
            title: rescheduleOnly ? "Não foi possível reagendar o plantão" : "Não foi possível editar o plantão",
            description: apiErrorMessage(error),
            status: "error",
            duration: 5000,
            isClosable: true,
          }),
      }
    );
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={close}
      title={rescheduleOnly ? "Reagendar plantão" : "Editar plantão"}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={rescheduleOnly ? "Reagendar" : "Salvar"}
      isSubmitting={mutation.isPending || conflictGuard.isChecking}
      footer={
        conflictGuard.hasConflicts ? (
          <ShiftConflictFooter
            onConfirm={conflictGuard.confirm}
            onBack={conflictGuard.dismiss}
            confirmLabel={rescheduleOnly ? "Reagendar mesmo assim" : "Salvar mesmo assim"}
            isSubmitting={mutation.isPending}
          />
        ) : undefined
      }
    >
      {conflictGuard.hasConflicts ? (
        <ShiftConflictWarning conflitos={conflictGuard.conflitos} />
      ) : (
        <Stack spacing={4}>
          {shift ? (
            <Text fontSize="sm" color="app-muted-fg">
              {shift.vinculo_nome} · <CurrencyText value={shift.valor} />
            </Text>
          ) : null}

          <FormControl isInvalid={Boolean(errors.data)}>
            <FormLabel>{rescheduleOnly ? "Nova data do plantão" : "Data do plantão"}</FormLabel>
            <Input type="date" {...register("data")} />
            <FormErrorMessage>{errors.data?.message}</FormErrorMessage>
          </FormControl>

          {rescheduleOnly && reagendamentos?.length > 0 ? (
            <Stack spacing={1}>
              <Divider />
              <Text fontSize="sm" fontWeight="medium" color="app-muted-fg">
                Já reagendado {reagendamentos.length}x
              </Text>
              {reagendamentos.map((r, i) => (
                <Text key={i} fontSize="sm" color="app-muted-fg">
                  {r.data_anterior} → {r.data_nova} (em {r.reagendado_em})
                </Text>
              ))}
            </Stack>
          ) : null}

          {rescheduleOnly ? null : (
            <>
              <HStack spacing={4} align="flex-start">
                <FormControl isInvalid={Boolean(errors.horaInicio)}>
                  <FormLabel>Hora de início</FormLabel>
                  <Input type="time" {...register("horaInicio")} />
                  <FormErrorMessage>{errors.horaInicio?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(errors.horas)}>
                  <FormLabel>Horas</FormLabel>
                  <NumberInput min={1} max={48}>
                    <NumberInputField {...register("horas")} />
                  </NumberInput>
                  <FormErrorMessage>{errors.horas?.message}</FormErrorMessage>
                </FormControl>
              </HStack>

              <FormControl isInvalid={Boolean(errors.valor)}>
                <FormLabel>Valor (R$)</FormLabel>
                <NumberInput min={0} precision={2}>
                  <NumberInputField {...register("valor")} />
                </NumberInput>
                <FormErrorMessage>{errors.valor?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={Boolean(errors.observacao)}>
                <FormLabel>Observação</FormLabel>
                <Textarea rows={2} maxLength={OBSERVACAO_MAX} {...register("observacao")} />
                <FormErrorMessage>{errors.observacao?.message}</FormErrorMessage>
              </FormControl>

              <Checkbox {...register("importante")}>Marcar como importante</Checkbox>
            </>
          )}
        </Stack>
      )}
    </ModalShell>
  );
}
